import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/v1`;

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (email, password, company_name) => axios.post(`${API}/auth/register`, { email, password, company_name }),
  getMe: () => axios.get(`${API}/auth/me`),

  // Wizard
  startWizard: () => axios.post(`${API}/wizard/start`),
  wizardStep: (step, user_response) => axios.post(`${API}/wizard/step`, { step, user_response }),

  // Agents
  getAgents: () => axios.get(`${API}/agents`),
  getAgent: (id) => axios.get(`${API}/agents/${id}`),
  createAgent: (data) => axios.post(`${API}/agents`, data),
  updateAgent: (id, data) => axios.patch(`${API}/agents/${id}`, data),
  deleteAgent: (id) => axios.delete(`${API}/agents/${id}`),

  // Conversations
  getConversations: (agentId, archived = false) => axios.get(`${API}/conversations`, { params: { agent_id: agentId, archived: archived || undefined } }),
  getTemplates: (all = false) => axios.get(`${API}/templates`, { params: { all: all || undefined } }),
  createTemplate: (data) => axios.post(`${API}/templates`, data),
  deleteTemplate: (sid) => axios.delete(`${API}/templates/${sid}`),
  startTemplateConversation: (phoneNumber, contentSid, variables) =>
    axios.post(`${API}/conversations/start-template`, { phone_number: phoneNumber, content_sid: contentSid, variables }),
  archiveConversation: (id, archived = true) => axios.patch(`${API}/conversations/${id}/archive`, { archived }),
  blockConversation: (id, blocked = true) => axios.patch(`${API}/conversations/${id}/block`, { blocked }),
  deleteConversation: (id) => axios.delete(`${API}/conversations/${id}`),
  getConversation: (id) => axios.get(`${API}/conversations/${id}`),
  getMessages: (conversationId) => axios.get(`${API}/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, content, media_url, media_type) => axios.post(`${API}/conversations/${conversationId}/messages`, { content, media_url, media_type }),
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  toggleBot: (conversationId, is_bot_active, assigned_to) =>
    axios.patch(`${API}/conversations/${conversationId}/toggle-bot`, { is_bot_active, assigned_to }),
  claimConversation: (conversationId) => axios.patch(`${API}/conversations/${conversationId}/claim`),
  assignConversation: (conversationId, userId) =>
    axios.patch(`${API}/conversations/${conversationId}/assign`, { user_id: userId }),

  // Team
  getTeam: () => axios.get(`${API}/team`),
  createTeamMember: (data) => axios.post(`${API}/team`, data),
  updateTeamMember: (id, data) => axios.patch(`${API}/team/${id}`, data),
  deleteTeamMember: (id) => axios.delete(`${API}/team/${id}`),

  // Webhooks
  getWebhooks: () => axios.get(`${API}/webhooks`),
  createWebhook: (data) => axios.post(`${API}/webhooks`, data),
  updateWebhook: (id, data) => axios.patch(`${API}/webhooks/${id}`, data),
  deleteWebhook: (id) => axios.delete(`${API}/webhooks/${id}`),
  testWebhook: (id) => axios.post(`${API}/webhooks/${id}/test`),

  // API Keys (public API)
  getApiKeys: () => axios.get(`${API}/api-keys`),
  createApiKey: (data) => axios.post(`${API}/api-keys`, data),
  revokeApiKey: (id) => axios.delete(`${API}/api-keys/${id}`),

  // Quick Replies
  getQuickReplies: () => axios.get(`${API}/quick-replies`),
  createQuickReply: (data) => axios.post(`${API}/quick-replies`, data),
  updateQuickReply: (id, data) => axios.patch(`${API}/quick-replies/${id}`, data),
  deleteQuickReply: (id) => axios.delete(`${API}/quick-replies/${id}`),

  // Contacts
  getContacts: (search) => axios.get(`${API}/contacts`, { params: { search: search || undefined } }),
  getContact: (id) => axios.get(`${API}/contacts/${id}`),
  updateContact: (id, data) => axios.patch(`${API}/contacts/${id}`, data),
  deleteContact: (id) => axios.delete(`${API}/contacts/${id}`),
  getContactConversations: (id) => axios.get(`${API}/contacts/${id}/conversations`),

  // Channels
  getChannels: () => axios.get(`${API}/channels`),
  createChannel: (data) => axios.post(`${API}/channels`, data),
  verifyChannel: (channelId, code) => axios.post(`${API}/channels/${channelId}/verify`, { verification_code: code }),
  resendCode: (channelId) => axios.post(`${API}/channels/${channelId}/resend-code`),
  deleteChannel: (channelId) => axios.delete(`${API}/channels/${channelId}`),
  connectChannel: (channelId, agentId) => axios.patch(`${API}/channels/${channelId}/connect?agent_id=${agentId}`),
  getSandboxInfo: () => axios.get(`${API}/channels/sandbox-info`),
  getSupportedCountries: () => axios.get(`${API}/channels/supported-countries`),
  searchAvailableNumbers: (countryCode, contains, areaCode) => 
    axios.get(`${API}/channels/available-numbers/${countryCode}`, { params: { contains, area_code: areaCode } }),
  purchaseNumber: (phoneNumber, countryCode) =>
    axios.post(`${API}/channels/purchase-number`, { phone_number: phoneNumber, country_code: countryCode }),
  createWebChannel: (name) => axios.post(`${API}/channels/web`, { name }),

  // WhatsApp Embedded Signup
  getWhatsAppSignupConfig: () => axios.get(`${API}/channels/whatsapp-signup-config`),
  whatsAppSignup: (data) => axios.post(`${API}/channels/whatsapp-signup`, data),
  getSenderStatus: (channelId) => axios.get(`${API}/channels/${channelId}/sender-status`),
  getChannelProfile: (channelId) => axios.get(`${API}/channels/${channelId}/profile`),
  updateChannelProfile: (channelId, data) => axios.patch(`${API}/channels/${channelId}/profile`, data),
  addExistingSender: (data) => axios.post(`${API}/channels/add-existing-sender`, data),

  // Company & Dashboard
  getCompany: () => axios.get(`${API}/company`),
  getDashboardStats: () => axios.get(`${API}/dashboard/stats`),
  getAnalytics: (days = 30) => axios.get(`${API}/analytics`, { params: { days } }),

  // Billing (Stripe MXN)
  getBillingPlans: () => axios.get(`${API}/billing/plans`),
  getBillingInfo: () => axios.get(`${API}/billing/info`),
  getTransactions: (limit = 50) => axios.get(`${API}/billing/transactions`, { params: { limit } }),
  createSetupIntent: () => axios.post(`${API}/billing/setup-intent`),
  addPaymentMethod: (paymentMethodId) => axios.post(`${API}/billing/add-payment-method`, { payment_method_id: paymentMethodId }),
  deletePaymentMethod: (paymentMethodId) => axios.delete(`${API}/billing/payment-method/${paymentMethodId}`),
  setDefaultPaymentMethod: (paymentMethodId) => axios.post(`${API}/billing/set-default-payment-method`, { payment_method_id: paymentMethodId }),
  subscribe: (planId) => axios.post(`${API}/billing/subscribe`, { plan_id: planId }),
  cancelSubscription: () => axios.post(`${API}/billing/cancel-subscription`),
  addBalance: (amountMxn) => axios.post(`${API}/billing/add-balance`, { amount_mxn: amountMxn }),
  buyPlanWithBalance: (planId) => axios.post(`${API}/billing/buy-plan-with-balance`, { plan_id: planId }),
  getNumberPrice: () => axios.get(`${API}/billing/number-price`),
  
  // Legacy Payments (Stripe Checkout)
  createCheckout: (plan_id, origin_url) => axios.post(`${API}/payments/checkout`, { plan_id, origin_url }),
  checkPaymentStatus: (session_id) => axios.get(`${API}/payments/status/${session_id}`),

  // Notifications
  getNotifications: (limit = 50, unreadOnly = false) => 
    axios.get(`${API}/notifications`, { params: { limit, unread_only: unreadOnly } }),
  markNotificationRead: (notificationId) => axios.patch(`${API}/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => axios.patch(`${API}/notifications/read-all`),
  getErrorLogs: (limit = 50) => axios.get(`${API}/notifications/errors`, { params: { limit } }),

  // Onboarding
  getOnboardingStatus: () => axios.get(`${API}/onboarding/status`),
  completeOnboarding: () => axios.post(`${API}/onboarding/complete`),
  updateNotificationSettings: (settings) => axios.patch(`${API}/settings/notifications`, settings),

  // Admin
  adminGetCompanies: () => axios.get(`${API}/admin/companies`),
  adminGetCompanyDetail: (companyId) => axios.get(`${API}/admin/companies/${companyId}`),
  adminSetUserRole: (userId, role) => axios.patch(`${API}/admin/users/${userId}/role`, { role }),
  adminAddBalance: (companyId, amountMxn, reason) =>
    axios.post(`${API}/admin/companies/${companyId}/add-balance`, { amount_mxn: amountMxn, reason }),
  adminSetPlan: (companyId, planId, reason) =>
    axios.post(`${API}/admin/companies/${companyId}/set-plan`, { plan_id: planId, reason }),
  adminGetStats: () => axios.get(`${API}/admin/stats`),
  adminGetTransactions: (limit = 200) => axios.get(`${API}/admin/transactions`, { params: { limit } }),
  adminGetAgents: () => axios.get(`${API}/admin/agents`),
  adminGetChannels: () => axios.get(`${API}/admin/channels`),
  adminGetSettings: () => axios.get(`${API}/admin/settings`),
  adminUpdateSettings: (data) => axios.patch(`${API}/admin/settings`, data),

};
